import {PredictService} from '../../../services/predict.service';
import {MapperService} from '../../../services/mapper.service';
import {
  multiPolygonToCartesianPolygonHierarchies,
  normalize2dMultipolygon,
  optimizePolygon
} from '../cesiumTools';
import {LayerDatasource, LayerLoadType, LayerType, MapperViewer} from '../types';
import * as Cesium from 'cesium';

export class ZoneLayer implements LayerDatasource {
  id = 'polygon';
  name = 'Limite communale';
  active = true;
  dataSource = new Cesium.CustomDataSource(this.name);
  type = LayerType.DataSource;
  loadType = LayerLoadType.Instant;
  showLabel = false;
  viewer: MapperViewer;

  cityRectangle: any;
  cartesians: any[];
  isSupervisedZone = false;

  constructor(private predictService: PredictService, private mapperService: MapperService) {}

  initialize(viewer: MapperViewer): void {
    this.viewer = viewer;
    viewer.dataSources.add(this.dataSource);
  }

  remove(): void {
    this.viewer.dataSources.remove(this.dataSource);
  }

  loadData(): void {
    if (!this.dataSource) {
      console.error('Viewer not initialized');
      return;
    }

    if (this.viewer.plan.id_user) {
      this.getZoneFromIdUser();
    } else {
      this.getZoneFromTCZ();
    }
  }

  getZoneFromIdUser(): void {
    this.mapperService.getSupervisedZone(this.viewer.plan.id_user).subscribe((res) => {
      if (!res.list.length) {
        // Aucune zone supervisée, l'utilisateur n'est pas un superviseur
        this.getZoneFromTCZ();
        return;
      }

      this.predictService.getPolygonUnionFromListZone(res.list).subscribe((geojson) => {
        this.isSupervisedZone = true;
        this.drawPolygon(geojson);
      });
    }, () => {
      // En cas d'erreur, on charge la zone de TCZ
      this.getZoneFromTCZ();
    });
  }

  getZoneFromTCZ(): void {
    if (!this.viewer.plan.code_zone || !this.viewer.plan.type_zone) {
      console.error('Zone non définie');
      return;
    }

    this.predictService.getPolygon(this.viewer.plan.code_zone, this.viewer.plan.type_zone).subscribe((data) => {
      this.drawPolygon(data);
    });
  }

  drawPolygon(data): any {
    if (!data.features.length) {
      return;
    }

    // Normaliser la géométrie en MultiPolygon
    const geometry = data.features[0].geometry;
    if (geometry.type === 'Polygon') {
      geometry.coordinates = [geometry.coordinates];
    }

    // Prévenir les polygones 3D
    geometry.coordinates = normalize2dMultipolygon(geometry.coordinates);

    this.cartesians = optimizePolygon(geometry.coordinates.flat(2))

    // Calculer le rectangle englobant
    this.cityRectangle = Cesium.Rectangle.fromCartesianArray(this.cartesians);

    // les polygones
    const cartesianMultiPolygon = multiPolygonToCartesianPolygonHierarchies(geometry.coordinates);

    // Créer le rectangle de base avec les trous
    this.createBaseRectangleWithHoles(cartesianMultiPolygon);

    // Créer les contours des polygones
    this.createPolygoneOutline(cartesianMultiPolygon);

    // Centrer la caméra
    this.focusOnZone();
  }

  public focusOnZone(): void {
      if (this.viewer.hasBeenCentered) {
        return;
      }

      const bs = Cesium.BoundingSphere.fromPoints(this.cartesians);

      this.viewer.camera.flyToBoundingSphere(bs, {
        offset: new Cesium.HeadingPitchRange(0, -Math.PI / 2, bs.radius * 2)
      });
  }

  private createPolygoneOutline(cartesianMultiPolygon: any[]): void {
    cartesianMultiPolygon.forEach(cartesianPolygon => {
      const polyEntity = {
        polyline: {
          positions: cartesianPolygon.positions,
          width: 3,
          material: this.isSupervisedZone
            ? Cesium.Color.fromCssColorString('#FFF')
            : Cesium.Color.fromCssColorString('#000'),
          clampToGround: true,
        },
      };
      this.dataSource.entities.add(polyEntity as any);
    });
  }

  private createBaseRectangleWithHoles(cartesianMultiPolygon: any[]): void {
    const rectPos = {
      ne: Cesium.Rectangle.northeast(this.cityRectangle),
      nw: Cesium.Rectangle.northwest(this.cityRectangle),
      se: Cesium.Rectangle.southeast(this.cityRectangle),
      sw: Cesium.Rectangle.southwest(this.cityRectangle),
    };

    const bigRectangle = [
      Cesium.Cartographic.toCartesian({...rectPos.ne, latitude: rectPos.ne.latitude - .1, longitude: rectPos.ne.longitude - .1} as any),
      Cesium.Cartographic.toCartesian({...rectPos.nw, latitude: rectPos.nw.latitude - .1, longitude: rectPos.nw.longitude + .1} as any),
      Cesium.Cartographic.toCartesian({...rectPos.sw, latitude: rectPos.sw.latitude + .1, longitude: rectPos.sw.longitude + .1} as any),
      Cesium.Cartographic.toCartesian({...rectPos.se, latitude: rectPos.se.latitude + .1, longitude: rectPos.se.longitude - .1} as any),
    ].map(pos => ({
      x: pos.x,
      y: pos.y,
      z: pos.z,
    }));

    const baseRectangle = {
      polygon: {
        hierarchy: {
          positions: bigRectangle,
          holes: cartesianMultiPolygon
        },
        material: Cesium.Color.WHITE.withAlpha(0.3),
      },
    };

    this.dataSource.entities.add(baseRectangle as any);
  }

  setActive(active: boolean): void  {
    this.active = active;
    this.dataSource.show = active;
  }


  onAfterPrint(): void {
  }

  onBeforePrint(): void {
  }
}
